import { AlertTriangle, Eye, ShieldCheck, FlaskConical, Sprout, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareButton } from "@/components/ShareButton";
import { SpeakButton } from "@/components/SpeakButton";
import { SaveOfflineButton } from "@/components/SaveOfflineButton";
import { pestSpeechText } from "@/lib/speech";
import { downloadPestReport } from "@/lib/report";

const sevStyle = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "high") return "bg-red-600 text-white";
  if (v === "medium") return "bg-amber-500 text-white";
  return "bg-emerald-600 text-white";
};

export function PestReport({ report, readOnly = false }) {
  const r = report.result || {};
  const healthy = (r.type || "").toLowerCase() === "healthy";
  return (
    <div className="space-y-6" data-testid="pest-report">
      <div className="flex flex-wrap justify-end gap-2">
        {!readOnly && <SpeakButton text={pestSpeechText(report)} language={report.language} prefetch />}
        {!readOnly && report.id && (
          <SaveOfflineButton item={{ id: `pest-${report.id}`, kind: "pest", title: r.problem || "Pest diagnosis", subtitle: `${report.crop || "Crop"} · ${r.severity || ""} severity`, payload: { ...report } }} />
        )}
        {!readOnly && report.id && (
          <ShareButton kind="pest_report" refId={report.id} title={`${r.problem}${report.crop ? ` on ${report.crop}` : ""}`}
            summary={`${r.severity} severity · ${r.urgency_note || r.cause || ""}`} />
        )}
        <Button data-testid="pest-download-pdf-btn" onClick={() => downloadPestReport(report)} variant="outline" className="border-stone-300">
          <FileText className="h-4 w-4 mr-2" /> Download treatment sheet (PDF)
        </Button>
      </div>
      <div className={`rounded-2xl p-6 sm:p-8 text-white ${healthy ? "bg-[#2D6A4F]" : "bg-[#78350F]"}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-70 font-semibold mb-1">{r.type || "Diagnosis"}{report.crop ? ` · ${report.crop}` : ""}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold" data-testid="pest-problem">{r.problem}</h2>
            <p className="mt-2 opacity-90 text-sm max-w-xl">{r.cause}</p>
          </div>
          <div className="flex gap-2">
            <span data-testid="pest-severity" className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${sevStyle(r.severity)}`}>{r.severity} severity</span>
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/20">{r.confidence} confidence</span>
          </div>
        </div>
        {r.urgency_note && (
          <div className="mt-5 flex items-center gap-2 bg-white/15 rounded-xl px-4 py-3 text-sm font-medium">
            {healthy ? <Clock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4 text-amber-300" />} {r.urgency_note}
          </div>
        )}
      </div>

      {r.symptoms_seen && (
        <div className="bg-[#EAF6EF] rounded-2xl p-5 flex gap-3">
          <Eye className="h-5 w-5 text-[#2D6A4F] shrink-0 mt-0.5" />
          <div><div className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">Seen in the photo</div><p className="text-sm text-stone-700 leading-relaxed">{r.symptoms_seen}</p></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {r.treatments?.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#2D6A4F]" /> Treatment steps</h3>
            <ol className="space-y-3">
              {r.treatments.map((t, i) => (
                <li key={i} data-testid={`pest-treatment-${i}`} className="flex gap-3">
                  <span className="shrink-0 h-6 w-6 rounded-full bg-[#EAF6EF] text-[#2D6A4F] font-mono-tech text-sm font-bold flex items-center justify-center">{i + 1}</span>
                  <div><div className="font-semibold text-emerald-950 text-sm">{t.step}</div><div className="text-sm text-stone-600">{t.detail}</div></div>
                </li>
              ))}
            </ol>
          </div>
        )}
        {r.products?.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[#D97706]" /> Products to use</h3>
            <ul className="space-y-3">
              {r.products.map((p, i) => (
                <li key={i} data-testid={`pest-product-${i}`} className="border-l-2 border-[#D97706] pl-3">
                  <div className="font-semibold text-emerald-950">{p.name}</div>
                  <div className="text-xs font-mono-tech text-[#78350F] mt-0.5">{p.dosage}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {r.prevention?.length > 0 && (
        <div className="bg-[#143D29] text-emerald-50 rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Sprout className="h-5 w-5 text-[#52B788]" /> Prevent it next season</h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {r.prevention.map((t, i) => <li key={i} className="flex gap-2 text-sm text-emerald-100/90"><span className="text-[#52B788] mt-0.5">•</span>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
